import type {
  JobGetListFilterType,
  JobItemExportListRequestType,
  JobType,
} from "../schemas/JobSchemas.ts";
import type { ReportWithDownloadV2Type } from "../schemas/ReportSchemas.ts";
import { getFilterObject, Resources } from "../util/index.ts";
import type {
  GetListRequestType,
  GetListResponseType,
  ResourceAdapterToAdapterQueryKeysType,
  ResourceAdapterType,
} from "../util/index.ts";
import { restAPI } from "./api.ts";

export interface JobAdapterType
  extends Pick<ResourceAdapterType<JobType>, "getList" | "getOne"> {
  getList: (
    params: GetListRequestType<JobGetListFilterType>
  ) => Promise<GetListResponseType<JobType>>;
  jobItem: {
    exportList: (
      params: JobItemExportListRequestType & { job_id: string }
    ) => Promise<ReportWithDownloadV2Type>;
  };
}

export const JobAdapter: JobAdapterType = {
  getList: async ({ page, pageSize, sortBy, sortDirection, filters }) => {
    const formattedFilters = getFilterObject(filters);
    return restAPI.nodeAPI.POST<GetListResponseType<JobType>>({
      endpoint: `/jobs/search`,
      body: {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: formattedFilters,
      },
    });
  },

  getOne: ({ id }) =>
    restAPI.nodeAPI
      .GET<JobType>({
        endpoint: `/jobs/${id}`,
        version: 2,
      })
      .then((resp) => ({
        data: resp,
      })),

  jobItem: {
    /**
     * Export job items
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/job/export_job_items
     */
    exportList: ({ filters, sortBy, sortDirection, job_id }) => {
      return restAPI.nodeAPI.POST<ReportWithDownloadV2Type>({
        endpoint: `/jobs/${job_id}/items/export`,
        body: {
          sort_by: sortBy,
          sort_direction: sortDirection,
          filter: getFilterObject(filters),
        },
      });
    },
  },
};

export const JobAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<JobAdapterType> =
  {
    getList: (params) => [Resources.JOB, "getList", params] as const,
    getOne: (params) => [Resources.JOB, "getOne", params.id] as const,
    jobItem: {
      exportList: (params) => [Resources.JOB_ITEM, "export", params] as const,
    },
  };
